import i18n from "@plugins/i18n.ts";
import moment from "moment";
import DateTimeFormatter from "@utils/datetime.formatter";
import { DATA_STATE } from "@plugins/modal/modal.constant";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import ErrorMixin from "@plugins/common/mixin/error.mixin";
import DateValidationMixin from "@plugins/common/mixin/date.validation.mixin";
import SecurityUtils from "@utils/security.utils";
import NotificationApi from "@apis/notification.api";
import NotificationUtils from "../service/notification.utils";
import DateTimeUtils from "@utils/datetime.utils";
import ROLE from "@configs/role";

export default {
  mixins: [ErrorMixin, DateValidationMixin],
  props: {
    params: { type: Object, default: null },
  },
  emits: ["create", "update", "delete"],
  data() {
    return {
      id: null,
      isLoading: true,
      kind: "",
      visible: true,
      lockService: null,
      notification: null,
      titleCharacterLimit: 50,
      formData: {},
      isProcessing: false,
      calendarOption: { dateFormat: "d M Y", allowInput: true },
      canEdit: false,
      canSubmit: false,
      scope: null,
      error: {
        title: "",
        group: "",
        formClass: "",
        content: "",
        startDate: "",
        startTime: "",
      },
      titles: [],
    };
  },
  computed: {
    showHeaderButton() {
      return this.kind === "editor" && this.notification;
    },
    isEdit() {
      return typeof this.notification !== "undefined" && this.notification !== null;
    },
  },
  methods: {
    handleSend() {
      this.isProcessing = true;
      this.setDataState(DATA_STATE.LOADING);
      if (this.validate()) {
        // listing to handle creation
        this.removeDirty(this.id);
        this.$emit("create", this.getRequestData(), this);
      } else {
        this.isProcessing = false;
        this.setDataState(DATA_STATE.ERROR);
        this.setErrorMessage(i18n.t("announcement_error_save"));
      }
    },
    handleUpdate() {
      this.isProcessing = true;
      this.setDataState(DATA_STATE.LOADING);
      if (this.validate()) {
        this.removeDirty(this.id);
        NotificationApi.saveNotification(this.getRequestData()).then((response) => {
          if (response.code === "error") {
            this.passiveApiFailHandler();
            return;
          }
          if (response.code === "ok") {
            this.setDataState(DATA_STATE.COMPLETED);

            let status = "ACTIVE";
            const currentTime = DateTimeFormatter.formatDateTimeAmPmToLocalDateTime(new Date());
            // only possible case that change start date from future to past/present
            if (this.formData.startDateTime > currentTime) {
              status = "UPCOMING";
            }

            this.$emit(
              "update",
              {
                uuid: this.notification.uuid,
                title: this.formData.title,
                startDateTime: this.formData.startDateTime,
                status: status,
              },
              this,
            );
            this.actionHidden();
            this.$toast.show("success", {
              title: this.$i18n.t("announcement_edit_success_toast_message"),
            });
          }
          this.isProcessing = false;
        }, this.passiveApiFailHandler);
      } else {
        this.isProcessing = false;
        this.setDataState(DATA_STATE.ERROR);
        this.setErrorMessage(i18n.t("announcement_error_save"));
      }
    },
    handleDelete() {
      // listing to handle deleting
      this.$emit("delete", this.notification.uuid, this);
    },
    handleTitleChange(value) {
      this.error.title = "";
      this.setDirty(this.id);
      this.formData.title = value;
    },
    handleMessageChange(value) {
      this.error.content = "";
      this.setDirty(this.id);
      this.formData.content = value;
    },
    /**
     * Date Time picker form changes
     */
    onStartDateChange(dateChange) {
      const value = dateChange;

      if (value === this.formData.startDate) {
        return;
      }

      this.error.startDate = "";
      this.setDirty(this.id);
      this.formData.startDate = value;
    },
    onUpdateStartTime(value) {
      this.error.startTime = "";
      this.setDirty(this.id);
      this.formData.startTime = value;
    },
    hasNotificationStarted(): boolean {
      if (!this.isEdit || !this.notification?.startDate) {
        return false;
      }
      const notificationStartDate = moment(
        DateTimeFormatter.formatLocalDateTimeToDateTime(this.notification.startDate),
      );
      return !notificationStartDate.isAfter(moment());
    },
    checkAndUpdateStartDateValue(): void {
      if (!this.hasNotificationStarted()) {
        return;
      }

      this.formData.startDate = DateTimeFormatter.formatLocalDateTimeToDateOnly(moment());
      const currentTimeAmPm = DateTimeFormatter.formatLocalDateTimeToTimeAmPm(moment());
      this.formData.startTime = DateTimeFormatter.formatTimeAmPmToTime24hr(currentTimeAmPm);
    },
    populateCommonFields() {
      this.formData.title = this.isEdit ? this.notification.title : "";
      this.formData.content = this.isEdit ? this.notification.content : "";
      this.formData.startDate = this.isEdit
        ? DateTimeFormatter.formatLocalDateTimeToDateOnly(this.notification.startDate)
        : DateTimeFormatter.formatLocalDateTimeToDateOnly(moment());
      const startTimeAmPm = this.isEdit
        ? DateTimeFormatter.formatLocalDateTimeToTimeAmPm(this.notification.startDate)
        : DateTimeFormatter.formatLocalDateTimeToTimeAmPm(moment());
      this.formData.startTime = DateTimeFormatter.formatTimeAmPmToTime24hr(startTimeAmPm);
      this.formData.notify = this.isEdit ? this.notification.notify : "false";
    },
    validate() {
      // to be overwrote
    },
    validateBasicFields() {
      let isValid = true;
      this.clearError();
      // validate title
      if (this.formData.title.trim() === "") {
        this.error.title = i18n.t("announcement_title_empty_message");
      }
      // validate content
      if (!this.$refs.richTextEditor.validate()) {
        this.error.content = i18n.t("announcement_content_empty_message");
      }

      this.validateDateTime();

      // validate title maximum character limit
      if (this.formData.title.trim().length > this.titleCharacterLimit) {
        isValid = false;
      }

      for (const key in this.error) {
        if (this.error[key] !== "") isValid = false;
      }
      return isValid;
    },
    validateDateTime() {
      // validate date time empty and format
      if (this.formData.startDate.trim() === "") {
        this.error.startDate = i18n.t("announcement_start_date_empty_error");
      } else if (NotificationUtils.dateFormatInvalid(this.formData.startDate)) {
        this.error.startDate = i18n.t("announcement_date_format_invalid_error");
      }

      if (this.formData.startTime.trim() === "") {
        this.error.startTime = i18n.t("announcement_start_time_empty_error");
      } else if (!DateTimeUtils.timeFormatValid(this.formData.startTime)) {
        this.error.startTime = i18n.t("announcement_time_format_invalid_error");
      }

      // validate date time range correntness
      const startTimeAmPm = DateTimeFormatter.formatTime24hrToTimeAmPm(this.formData.startTime);
      const startDateTime = `${this.formData.startDate} ${startTimeAmPm}`;

      this.formData.startDateTime =
        DateTimeFormatter.formatDateTimeAmPmToLocalDateTime(startDateTime);
    },
    clearError() {
      // clear all the form's errors
      for (const key in this.error) {
        this.error[key] = "";
      }
    },
    handleLockRetrievalFail(response) {
      const title = this.$i18n.t("resource_lock_title", {
        resource: "Notification",
      });
      const content = this.$i18n.t("resource_lock_content", {
        resource: LOCKABLE_RESOURCE_TYPE.NOTIFICATION,
        user: SecurityUtils.sanitizeText(response.user),
      });

      const props = {
        title: title,
        content: content,
        primaryBtnLabel: this.$i18n.t("button_ok"),
        use_secondaryButton: false,
        actionPrimary: () => {
          // to be empty
        },
        actionHidden: () => {
          this.actionHidden();
        },
        actionHideRequest: () => {
          this.actionHidden();
        },
      };
      this.$modal.showMessage("warning", props);
    },
  },
  created() {
    this.canEdit = this.params.data.canEdit;
    this.canSubmit = this.params.data.canSubmit;
    this.scope = this.params.data.scope;
    this.kind = this.params.kind;
    this.notification = this.params.data.notification;

    if (this.params.kind === "editor" && this.notification) {
      this.titles.push(i18n.t("announcement_edit_title"));
    } else {
      switch (this.params.data.role) {
        case ROLE.TEACHER:
          const isSelectedGroup = this.params.data.preselectGroup;
          if (isSelectedGroup === null || isSelectedGroup === undefined) {
            this.titles.push(i18n.t("announcement_new_class_title"));
          } else {
            this.titles.push(
              isSelectedGroup.publicDomain
                ? i18n.t("announcement_new_commmunity_title")
                : i18n.t("announcement_new_class_title"),
            );
          }
          break;
        case ROLE.SCHOOL_ADMIN:
          this.titles.push(i18n.t("announcement_new_school_title"));
          break;
        case ROLE.VLE_ADMIN:
          this.titles.push(i18n.t("announcement_new_system_title"));
          break;
      }
    }
  },
};
